/**
 * Phase 66 Plan 02 — rate-limit banner store.
 *
 * Minimal pub/sub store (no Zustand dep — plain module + Set of listeners).
 * `apiClient` calls `rateLimitStore.trigger(retryAfter)` on 429; the
 * `<RateLimitBanner>` component subscribes via React 18 useSyncExternalStore.
 *
 * Replaces the global `window.AdminRateBanner` IIFE singleton from
 * `js/admin/rate-limit-banner.js` (Phase 34 Plan 03).
 *
 * Threat model: T-66-02-07 — banner stuck active is mitigated by the
 * countdown timeout in <RateLimitBanner> calling `clear()` at 0.
 */

export type RateLimitState = {
  active: boolean;
  retryAfter: number;
};

type Listener = (state: RateLimitState) => void;

const listeners = new Set<Listener>();
let state: RateLimitState = { active: false, retryAfter: 0 };

function notify() {
  for (const listener of listeners) {
    try {
      listener(state);
    } catch {
      // Swallow listener errors — store reliability over component crashes.
    }
  }
}

export const rateLimitStore = {
  trigger(retryAfter: number): void {
    state = { active: true, retryAfter: Math.max(1, Math.floor(retryAfter)) };
    notify();
  },
  clear(): void {
    state = { active: false, retryAfter: 0 };
    notify();
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getState(): RateLimitState {
    return state;
  },
};
